/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import DataExportUtil from "../../utilities/data-export-util";
import UserService from '../../utilities/services/user-service';
import OrganizationService from '../../utilities/services/organization-service';
import { useAuthState } from '../../utilities/contexts/auth-state-context';
import { Button } from '../../components/button/button';
import { useTranslation } from 'react-i18next';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import UserLatestMskScoreService from '../../utilities/services/user-latest-msk-score-service';

const ExportPage: React.FC = () => {
    const { t } = useTranslation();

    const exportOptions = [
        { value: 'portalUsers', label: t('pages.dataExport.portalUsers') },
        { value: 'appUsers', label: t('pages.dataExport.appUsers') },
        { value: 'userMskData', label: 'User MSK Data' }
    ];
    const { state } = useAuthState();
    const [selectedExport, setSelectedExport] = useState<string>('portalUsers');
    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    const [downloadUrl, setDownloadUrl] = useState<string>();
    const [downloadFileName, setDownloadFileName] = useState<string>();

    const handleGenerate = async () => {
        setIsGenerating(true);

        const snackbarKey = enqueueSnackbar(
            t('pages.dataExport.pleaseWait').toString(),
            { variant: "info", autoHideDuration: 30000 }
        );

        try {
            const users = await UserService.getAll();
            const orgs = await OrganizationService.getAll();

            let blob: Blob | null = null;
            let fileName = '';

            if (selectedExport === 'portalUsers') {
                const rows = DataExportUtil.getHubspotUserDataRows(users, orgs, true);
                const csv = DataExportUtil.generateHubspotUserCsv(rows);
                blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                fileName = 'hubspotPortalUsersData.csv';
            }

            if (selectedExport === 'appUsers') {
                const rows = DataExportUtil.getHubspotUserDataRows(users, orgs, false);
                const csv = DataExportUtil.generateHubspotUserCsv(rows);
                blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                fileName = 'hubspotAppUsersData.csv';
            }

            if (selectedExport === 'userMskData') {
                const mskScores = await UserLatestMskScoreService.getAll(); // or your actual service

                const rows = DataExportUtil.getUserMskDataRows(mskScores, users);
                const csv = DataExportUtil.generateUserMskDataCsv(rows);

                blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                fileName = 'userMskData.csv';
            }

            if (blob) {
                setDownloadUrl(URL.createObjectURL(blob));
                setDownloadFileName(fileName);
            }

            closeSnackbar(snackbarKey);

            enqueueSnackbar(t('pages.dataExport.csvComplete'), {
                variant: "toast",
                width: "450px"
            });

        } catch (err) {
            // also close on error
            closeSnackbar(snackbarKey);

            enqueueSnackbar(t('general.error'), {
                variant: "error"
            });

        } finally {
            setIsGenerating(false);
        }
    };

    const handleExportChange = (value: string) => {
        setSelectedExport(value);
        setDownloadUrl(undefined);
        setDownloadFileName(undefined);
    };

    if (!state.user?.isSuperAdmin) {
        return <Redirect to="/" />;
    }

    return (
        <>
            <div>
                <h3>{t('pages.dataExport.title')}</h3>

                {/* Controls */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <select
                        value={selectedExport}
                        onChange={(e) => handleExportChange(e.target.value)}
                    >
                        {exportOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>

                    <Button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        buttonText={t('buttons.btn_generate')}
                    />
                </div>

                {/* Download */}
                <div style={{ marginTop: '1rem' }}>
                    {downloadUrl && (
                        <a
                            style={{
                                background: 'rgb(18, 22, 34)',
                                borderRadius: '900px',
                                padding: '6px 20px',
                                color: 'white',
                            }}
                            href={downloadUrl}
                            download={downloadFileName}
                        >
                            {t('buttons.btn_download')}
                        </a>
                    )}
                </div>
            </div>
        </>
    );
}

export default ExportPage;
